import { useRef } from 'react'
import { JUMP_TO_START_OVER_ID } from '../../modules/preview/components/const'

import DevLog from '../../modules/devlog/Module'
import PreviewModule from '../../modules/preview/Module'

import { ISessionProgress, ISessionRefreshView, ISessionUseProgressState } from '../types'
import { ActionType, SurveyMode } from '../../actions/types'

function buildSessionProgressModel(refreshView: ISessionRefreshView): ISessionProgress {
  let current = 0 // elapsed time since start of the action in seconds
  let total = 0 // length of interview in seconds
  let currentQuestionBase = 0 // question start time from `current` in seconds
  let currentQuestionTime = 0 // question time in seconds
  let currentQuestionRealTime = 0 // question real time in seconds
  let currentQuestionProgress = 0 // question progress in seconds

  const update = () => {
    DevLog._log(
      'SW[progress]:',
      `${current} / ${total} [ ${currentQuestionBase} | ${currentQuestionProgress} / ${currentQuestionTime} (${currentQuestionRealTime}) ]`
    )
    refreshView?.()
  }

  const resetCurrentQuestion = () => {
    currentQuestionBase = 0
    currentQuestionTime = 0
    currentQuestionRealTime = 0
    currentQuestionProgress = 0
  }
  resetCurrentQuestion()

  const progress: ISessionProgress = {
    get current() {
      return Math.min(total, current + currentQuestionBase + currentQuestionProgress)
    },
    get total() {
      return total
    },
    updateFromAction(action, target) {
      switch (action?.action) {
        case ActionType.ASK: {
          resetCurrentQuestion()
          if (target) {
            switch (action?.mode) {
              case SurveyMode.XRAYS:
              case SurveyMode.BRIDGE: {
                // use preview navigation options as a source of truth for progress
                const navigationOptions =
                  action?.preview?.navigation_options ||
                  (PreviewModule.loaded
                    ? PreviewModule.jumpToQuestionOptions.filter(option => JUMP_TO_START_OVER_ID !== option.id)
                    : null)
                if (Array.isArray(navigationOptions) && navigationOptions.length) {
                  total = action?.data?.time?.loi || 100
                  current = 0
                  const questionIndex = navigationOptions.findIndex(option => option.id === target)
                  if (0 <= questionIndex) {
                    const distributedQuestionTime = total / navigationOptions.length
                    current = distributedQuestionTime * questionIndex
                    currentQuestionBase = 0
                    currentQuestionTime = distributedQuestionTime
                  }
                }
                // and use action question as a source of truth for question time
                const questions = action?.data?.questions
                if (Array.isArray(questions)) {
                  const question = questions.find(question => question.id === target)
                  if (question) {
                    currentQuestionRealTime = question.time || 0
                  }
                } else {
                  currentQuestionRealTime = currentQuestionTime
                }
                if (0 === currentQuestionTime) {
                  currentQuestionTime = currentQuestionRealTime
                }
                break
              }
              default: {
                // case SurveyMode.RESPONDENT:
                // case SurveyMode.RESEARCHER:
                // use action as a source of truth
                const time = action?.data?.time
                if (time) {
                  total = time.loi
                  current = time.elapsed
                }
                const questions = action?.data?.questions
                if (Array.isArray(questions)) {
                  for (const question of questions) {
                    if (question.id === target) {
                      currentQuestionTime = question.time || 0
                      currentQuestionRealTime = currentQuestionTime
                      break
                    } else {
                      currentQuestionBase += question.time || 0
                    }
                  }
                }
                break
              }
            }
          }
          update()
          break
        }
        case ActionType.END: {
          resetCurrentQuestion()
          if (0 === total) {
            total = 100
          }
          current = total
          update()
          break
        }
      }
      return progress
    },
    get questionTime() {
      return currentQuestionRealTime
    },
    get questionProgressSeconds() {
      return 0 === currentQuestionTime || 0 === currentQuestionRealTime
        ? 0
        : currentQuestionRealTime === currentQuestionTime
        ? currentQuestionProgress
        : (currentQuestionProgress * currentQuestionRealTime) / currentQuestionTime
    },
    set questionProgressSeconds(seconds) {
      currentQuestionProgress =
        0 === currentQuestionTime || 0 === currentQuestionRealTime
          ? 0
          : Math.round(
              Math.max(0, Math.min((seconds * currentQuestionTime) / currentQuestionRealTime, currentQuestionTime)) *
                100
            ) / 100
      update()
    },
    advanceQuestionSeconds(seconds) {
      progress.questionProgressSeconds = progress.questionProgressSeconds + seconds
      return progress
    },
    rollbackQuestionSeconds(seconds) {
      progress.questionProgressSeconds = progress.questionProgressSeconds - seconds
      return progress
    }
  }
  return progress
}

const useProgressState: ISessionUseProgressState = refreshView => {
  const progressRef = useRef<ISessionProgress>(null)
  if (null == progressRef.current) {
    progressRef.current = buildSessionProgressModel(refreshView)
  }

  return { progressRef } as const
}
export default useProgressState
